import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import "./components/profile.css";
import EmployeeDetails from "./components/EmployeeDetails.js";
import EmployeeAddress from "./components/EmployeeAddress.js";
import EmployeePersonalDetails from "./components/EmployeePersonalDetails.js";
import EmployeeHistory from "./components/EmploymentHistory.js";
import GovernmentDocs from "./components/EmployeeGovernmentDoc.js";
import EmployeeEducationBackground from "./components/EducationBackground.js";
import EmployeeEmergencyContact from "./components/EmergencyContact.js";
import EmployeeClearance from "../assets/components/EmployeeClearance";
import "../assets/assets.css";
import useAssetAccess from "../../hooks/useAssetAccess";
import EmployeeLearningRecord from "../learning/components/EmployeeLearningRecord";
import EmployeeApprovers from "../approvers/EmployeeApprovers";
import usePermissions from "../../hooks/usePermissions";
import { useEmployee } from "../../api/queries/employeeQueries";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/apiError";

/* ===================== COLLAPSIBLE SECTION COMPONENT ===================== */
// Content stays mounted while collapsed so unsaved edits aren't lost and
// "Save Profile" can still reach every section through its ref.
const CollapsibleSection = ({
  title,
  icon,
  description,
  children,
  isOpen,
  onToggle,
  collapsible = true,
}) => {
  const Header = collapsible ? "button" : "div";
  return (
    <section className="profile-section">
      <Header
        {...(collapsible
          ? { type: "button", onClick: onToggle, "aria-expanded": isOpen }
          : {})}
        className={`profile-section-header ${collapsible ? "" : "is-static"}`}
      >
        <span className="profile-section-title">
          <i className={`bi ${icon}`}></i>
          {title}
          {description && (
            <span className="profile-section-desc d-none d-md-inline">
              · {description}
            </span>
          )}
        </span>
        {collapsible && (
          <i
            className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} fs-080`}
          ></i>
        )}
      </Header>
      <div className="profile-section-body" hidden={collapsible && !isOpen}>
        {children}
      </div>
    </section>
  );
};

const SECTIONS = [
  {
    key: "personal",
    title: "Personal Details",
    icon: "bi-person-vcard",
    description: "Gender, birth date, contact numbers",
  },
  {
    key: "address",
    title: "Address Information",
    icon: "bi-geo-alt",
    description: "Current and permanent address",
  },
  {
    key: "contacts",
    title: "Employee Contacts",
    icon: "bi-telephone-outbound",
    description: "Emergency and other contacts",
  },
  {
    key: "files",
    title: "Employee Files",
    icon: "bi-shield-check",
    description: "Government IDs and documents",
  },
  {
    key: "history",
    title: "Employment History",
    icon: "bi-briefcase",
    description: "Previous employers",
  },
  {
    key: "education",
    title: "Educational Background",
    icon: "bi-mortarboard",
    description: "Schools and attachments",
  },
];

// Approval chain for DTR, overtime and evaluations (Admin/HR)
const APPROVERS_SECTION = {
  key: "approvers",
  title: "Approvers",
  icon: "bi-diagram-3",
  description: "Who approves this employee's DTR, overtime and evaluations",
};

// Seminars and skills of this employee (Admin/HR)
const LEARNING_SECTION = {
  key: "learning",
  title: "Learning",
  icon: "bi-mortarboard-fill",
  description: "Seminars attended and skills",
};

// Shown only to people who manage assets (Admin/HR, branch managers)
const ASSETS_SECTION = {
  key: "assets",
  title: "Assets & Clearance",
  icon: "bi-pc-display",
  description: "Company assets this employee holds",
};

/* ===================== MAIN PROFILE PAGE ===================== */
const EmployeeProfile = () => {
  const detailsRef = useRef();
  const personalRef = useRef();
  const addressRef = useRef();
  const emergencyRef = useRef();

  const navigate = useNavigate();
  const { id } = useParams();

  const [isSaving, setIsSaving] = useState(false);
  const [saveResults, setSaveResults] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const { canManageAssets } = useAssetAccess();
  // Approvers / learning record are HR-level (employees with COMPANY scope)
  const isCompanyWide = usePermissions().companyWide("employees");
  const sections = [
    ...SECTIONS,
    ...(canManageAssets ? [ASSETS_SECTION] : []),
    ...(isCompanyWide && id && id !== "new"
      ? [APPROVERS_SECTION, LEARNING_SECTION]
      : []),
  ];

  // Outside the role's scope (403) or not found (404): say so instead of an empty form
  const employeeQuery = useEmployee(id && id !== "new" ? id : null);

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const setAllSections = (open) =>
    setOpenSections(Object.fromEntries(sections.map((sec) => [sec.key, open])));
  const allOpen = sections.every((sec) => openSections[sec.key]);

  const handleGlobalSave = async () => {
    setIsSaving(true);
    setSaveResults(null);

    const sections = [
      { name: "Employment Details", ref: detailsRef },
      { name: "Personal Details", ref: personalRef },
      { name: "Address Information", ref: addressRef },
      { name: "Emergency Contacts", ref: emergencyRef },
    ];

    const savePromises = sections.map(async (section) => {
      if (section.ref.current?.submit) {
        try {
          await section.ref.current.submit();
          return { name: section.name, status: "success" };
        } catch (error) {
          return { name: section.name, status: "error" };
        }
      }
      return null;
    });

    const results = await Promise.all(savePromises);
    setSaveResults(results.filter((r) => r !== null));
    setIsSaving(false);

    // Keep it in the middle for 5 seconds or until closed
    setTimeout(() => setSaveResults(null), 5000);
  };

  if (employeeQuery.isError) {
    const code = getApiErrorStatus(employeeQuery.error);
    return (
      <Layout>
        <div className="container-fluid py-5 px-lg-4">
          <div className="approval-empty mx-auto access-state">
            <i className="bi bi-shield-lock"></i>
            <strong className="d-block fs-100 text-navy mb-1">
              {code === 404 ? "Employee not found" : "Access denied"}
            </strong>
            {code === 403 || code === 404
              ? "This employee is outside your access, or doesn't exist."
              : getApiErrorMessage(
                  employeeQuery.error,
                  "Could not load this employee.",
                )}{" "}
            <button
              type="button"
              className="btn btn-link p-0 align-baseline"
              onClick={() => navigate("/employee")}
            >
              Back to employees
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* 1. GLOBAL STATUS NOTIFICATION (FORCED CENTER) */}
      {saveResults && (
        <div
          className="position-fixed top-0 start-0 w-100 vh-100 d-flex align-items-center justify-content-center save-summary-backdrop"
          onClick={() => setSaveResults(null)}
        >
          {/* Centered Summary Card */}
          <div
            className="bg-white shadow-lg rounded-3 border-0 overflow-hidden save-summary-card"
            onClick={(e) => e.stopPropagation()} // Prevents closing when clicking inside the card
          >
            {/* Modal Header */}
            <div className="bg-dark text-white px-4 py-3 d-flex justify-content-between align-items-center">
              <span className="fw-bold fs-085 ls-005em">
                <i className="bi bi-info-circle me-2 text-info"></i>SAVE SUMMARY
              </span>
              <i
                className="bi bi-x-lg cursor-pointer fs-090"
                onClick={() => setSaveResults(null)}
              ></i>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              {saveResults.map((res, index) => (
                <div
                  key={index}
                  className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom border-light"
                >
                  <span className="text-secondary fw-semibold fs-090">
                    {res.name}
                  </span>
                  <div className="d-flex align-items-center">
                    <span className={`me-2 fw-bold text-uppercase fs-070`}>
                      {res.status === "success" ? (
                        <span className="text-success">Success</span>
                      ) : (
                        <span className="text-danger">Failed</span>
                      )}
                    </span>
                    <i
                      className={`bi ${res.status === "success" ? "bi-check-circle-fill text-success" : "bi-x-circle-fill text-danger"} fs-5`}
                    ></i>
                  </div>
                </div>
              ))}

              <button
                className="btn btn-dark w-100 mt-2 fw-bold py-2 shadow-sm rounded-8"
                onClick={() => setSaveResults(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Header Bar */}
      <div className="profile-page-header px-1">
        <div>
          <h2>{!id ? "Create Employee" : "Employee Profile"}</h2>
          <p className="subtitle">
            {!id
              ? "Fill in the general information first. Other sections unlock after saving."
              : "Edit a section with its Edit button, or use Save Profile to save all sections being edited."}
          </p>
        </div>

        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-sm px-3 fw-semibold d-flex align-items-center profile-btn-white h-35px rounded-8"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left me-2"></i> Back
          </button>

          {id && (
            <button
              type="button"
              className="btn btn-sm px-3 fw-semibold d-flex align-items-center profile-btn-white h-35px rounded-8"
              onClick={() => setAllSections(!allOpen)}
            >
              <i
                className={`bi ${allOpen ? "bi-arrows-collapse" : "bi-arrows-expand"} me-2`}
              ></i>
              {allOpen ? "Collapse all" : "Expand all"}
            </button>
          )}

          {id && (
            <button
              type="button"
              className="btn btn-success btn-sm px-3 fw-semibold d-flex align-items-center h-35px rounded-8"
              onClick={handleGlobalSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving Profile...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i> Save Profile
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* 3. Main Content Areas */}
      <CollapsibleSection
        title="General Information"
        icon="bi-person-badge"
        collapsible={false}
      >
        <EmployeeDetails ref={detailsRef} />
      </CollapsibleSection>

      {id && (
        <div className="pb-5">
          {sections.map((sec) => (
            <CollapsibleSection
              key={sec.key}
              title={sec.title}
              icon={sec.icon}
              description={sec.description}
              isOpen={!!openSections[sec.key]}
              onToggle={() => toggleSection(sec.key)}
            >
              {sec.key === "personal" && (
                <EmployeePersonalDetails ref={personalRef} />
              )}
              {sec.key === "address" && <EmployeeAddress ref={addressRef} />}
              {sec.key === "contacts" && (
                <EmployeeEmergencyContact ref={emergencyRef} />
              )}
              {sec.key === "files" && <GovernmentDocs />}
              {sec.key === "history" && <EmployeeHistory />}
              {sec.key === "education" && <EmployeeEducationBackground />}
              {sec.key === "assets" && (
                <EmployeeClearance employeeId={id} canManage />
              )}
              {sec.key === "learning" && (
                <EmployeeLearningRecord employeeId={id} />
              )}
              {sec.key === "approvers" && <EmployeeApprovers employeeId={id} />}
            </CollapsibleSection>
          ))}
        </div>
      )}
    </Layout>
  );
};

export default EmployeeProfile;
